// Effects in the sky: the aurora, starlight, shooting stars, distant
// lightning and the shadows of passing clouds. Light lands on the picture with
// screen, so it only ever brightens, and is strongest where the sky is dark.

import { css, layer, noise1, rng, type RGB } from "../scenes";
import { clamp01, dither, keepToSky, lift, mixRGB, noiseTexture, smooth, softDot, speckle, turnHue, wrap, type Effect } from "./kit";
import { average, horizon, inSky, lumAt, skyDepth, skyMask, specks } from "./map";

// ---------------------------------------------------------------- aurora

/** The drawn Aurora's own colors and bands, which are the standard every effect is held to. */
const AURORA: RGB[] = [
  [114, 240, 198],
  [67, 198, 172],
  [138, 184, 255],
  [199, 155, 255],
];
const BANDS = [
  { y: 0.23, amp: 0.07, f: 1.6, sp: 0.07, hgt: 0.3, a: 0.2 },
  { y: 0.28, amp: 0.05, f: 2.3, sp: -0.05, hgt: 0.22, a: 0.16 },
  { y: 0.18, amp: 0.06, f: 1.2, sp: 0.04, hgt: 0.19, a: 0.1 },
  { y: 0.15, amp: 0.045, f: 2.8, sp: 0.06, hgt: 0.13, a: 0.08 },
];

/** One column of a curtain: nothing at the top, its color three quarters down, gone at its foot. */
function curtain(rgb: RGB): HTMLCanvasElement {
  const [c, ctx] = layer(1, 256);
  const g = ctx.createLinearGradient(0, 0, 0, 256);
  g.addColorStop(0, css(rgb, 0));
  g.addColorStop(0.75, css(rgb, 1));
  g.addColorStop(1, css(rgb, 0));
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, 1, 256);
  return c;
}

export const aurora: Effect = {
  fps: 24,
  // drawn small, so the browser's scaling softens the columns into curtains
  res: 0.35,
  blend: "screen",
  build(env) {
    const colors = env.color ? [env.color, turnHue(env.color, 22), turnHue(env.color, -48), turnHue(env.color, 75)] : AURORA;
    const strips = BANDS.map((_, i) => curtain(colors[i]));
    const step = Math.max(2, Math.round(env.w / 260));
    // only where the sky is really bright (the moon, a city's glow) do the lights fade, as the eye would lose them there
    const dark = Array.from({ length: Math.ceil(env.w / step) + 1 }, (_, k) => (env.map ? clamp01((0.8 - lumAt(env.map, (k * step) / env.w, 0.2)) / 0.5) : 1));
    // over a photograph the lights sit behind its trees and hills, and above its own horizon
    const mask = env.map ? skyMask(env.map) : null;
    const raise = env.map ? Math.min(1.3, Math.max(0.8, skyDepth(env.map) / 0.5)) : 1;
    // rays: brighter and fainter runs along the curtain that flow into each other and drift slowly sideways
    const rays = noise1(env.seed * 3 + 11);
    return { strips, step, dark, mask, raise, rays };
  },
  frame(ctx, t, state, env) {
    const { strips, step, dark, mask, raise, rays } = state as { strips: HTMLCanvasElement[]; step: number; dark: number[]; mask: HTMLCanvasElement | null; raise: number; rays: (x: number) => number };
    const { w, h } = env;
    ctx.clearRect(0, 0, w, h);
    ctx.globalCompositeOperation = "lighter";
    // a photograph's sky is lighter than the drawn scene's near-black, so the lights are stronger over one
    const k = (0.55 + env.strength * 0.9) * (mask ? 1.5 : 1);
    BANDS.forEach((b, bi) => {
      for (let x = 0, col = 0; x < w; x += step, col++) {
        const u = x / w;
        const yc = h * (b.y * raise + Math.sin(u * Math.PI * b.f + t * b.sp * 6) * b.amp + Math.sin(u * 9.3 + t * 0.21) * 0.012);
        const hh = h * b.hgt * (0.65 + 0.35 * Math.sin(u * 7.1 + t * 0.33 + b.f));
        const ray = 0.86 + 0.14 * rays(col * 0.32 + bi * 37 + t * 0.12);
        const a = b.a * (0.6 + 0.4 * Math.sin(u * 13 + t * 0.6)) * ray * k * dark[col];
        if (a <= 0.004) continue;
        ctx.globalAlpha = Math.min(1, a);
        // columns meet exactly: an overlap would add up into a bright line every column
        ctx.drawImage(strips[bi], x, yc - hh, step, hh * 1.2);
      }
    });
    ctx.globalCompositeOperation = "source-over";
    keepToSky(ctx, mask, w, h);
  },
};

// ---------------------------------------------------------------- stars

/** A star's thin cross of light, as a camera catches the brightest. */
function cross(rgb: RGB, r: number): HTMLCanvasElement {
  const size = Math.max(8, Math.ceil(r * 2));
  const [c, ctx] = layer(size, size);
  const m = size / 2;
  for (const [dx, dy] of [
    [1, 0],
    [0, 1],
  ]) {
    const g = ctx.createLinearGradient(m - dx * m, m - dy * m, m + dx * m, m + dy * m);
    g.addColorStop(0, css(rgb, 0));
    g.addColorStop(0.5, css(rgb, 0.9));
    g.addColorStop(1, css(rgb, 0));
    ctx.fillStyle = g;
    if (dx) ctx.fillRect(0, m - 0.6, size, 1.2);
    else ctx.fillRect(m - 0.6, 0, 1.2, size);
  }
  return c;
}

interface Star {
  x: number;
  y: number;
  b: number;
  s: number;
  p: number;
  own: boolean;
  big: boolean;
}

export const stars: Effect = {
  fps: 15,
  res: 1,
  blend: "screen",
  build(env) {
    const r = rng(env.seed * 7 + 3);
    const want = Math.round(60 + 140 * env.strength);
    // the picture's own stars first, so it is the sky in the photograph that twinkles
    const found = env.map ? specks(env.map, [0, 0, 1, 0.75], { max: want, min: 0.16, lift: 0.04, darkAround: 0.45, inSky: true }) : [];
    const list: Star[] = found.map((p) => ({ x: p.x * env.w, y: p.y * env.h, b: clamp01(0.35 + p.b * 2.2), s: 0.35 + r() * 0.85, p: r() * 6.283, own: true, big: false }));
    // a sky with few stars of its own gets faint ones where it is darkest
    const extra = Math.max(0, Math.round(want * 0.55) - list.length);
    for (let i = 0, tries = 0; i < extra && tries < extra * 25; tries++) {
      const x = r();
      const y = r() * r() * 0.7;
      if (env.map && (lumAt(env.map, x, y) > 0.3 || y > env.map.skyline[Math.min(env.map.fw - 1, Math.floor(x * env.map.fw))] - 0.03)) continue;
      list.push({ x: x * env.w, y: y * env.h, b: 0.18 + r() * 0.3, s: 0.3 + r() * 0.7, p: r() * 6.283, own: false, big: false });
      i++;
    }
    // the brightest few carry a cross
    [...list].sort((a, b) => b.b - a.b).slice(0, Math.max(2, Math.round(list.length * 0.06))).forEach((s) => (s.big = true));
    const tint = env.color ?? [236, 242, 255];
    return { list, dot: softDot(tint, 3.4 * env.u, 0.12), star: cross(lift(tint, 0.3), 9 * env.u) };
  },
  frame(ctx, t, state, env) {
    const { list, dot, star } = state as { list: Star[]; dot: HTMLCanvasElement; star: HTMLCanvasElement };
    ctx.clearRect(0, 0, env.w, env.h);
    const k = 0.55 + env.strength * 0.7;
    for (const s of list) {
      const tw = 0.5 + 0.5 * Math.sin(t * s.s + s.p);
      // its own stars flare softly on top of what the picture shows; added ones rise and fall from faint
      const a = (s.own ? s.b * (0.12 + 0.88 * tw * tw) : s.b * (0.35 + 0.65 * tw * tw)) * k;
      if (a < 0.02) continue;
      const size = dot.width * (0.7 + s.b * 0.6);
      ctx.globalAlpha = Math.min(1, a);
      ctx.drawImage(dot, s.x - size / 2, s.y - size / 2, size, size);
      if (s.big) {
        ctx.globalAlpha = Math.min(1, a * 0.55);
        ctx.drawImage(star, s.x - star.width / 2, s.y - star.height / 2);
      }
    }
    ctx.globalAlpha = 1;
  },
};

// ---------------------------------------------------------------- shooting stars

interface Meteor {
  t0: number;
  x: number;
  y: number;
  dx: number;
  dy: number;
  v: number;
  life: number;
  len: number;
  b: number;
}

export const meteors: Effect = {
  fps: 30,
  res: 1,
  blend: "screen",
  build(env) {
    const r = rng(env.seed * 11 + 5);
    return { r, next: 2.5 + r() * 3, live: [] as Meteor[], drawn: false, head: softDot(lift(env.color ?? [226, 236, 255], 0.4), 5 * env.u, 0.2), mask: env.map ? skyMask(env.map) : null };
  },
  frame(ctx, t, state, env) {
    const s = state as { r: () => number; next: number; live: Meteor[]; drawn: boolean; head: HTMLCanvasElement; mask: HTMLCanvasElement | null };
    const { w, h, u } = env;
    if (t >= s.next) {
      // somewhere high in open sky, the darker the better, falling at a shallow angle
      let x = 0.5;
      let y = 0.12;
      let best = 2;
      for (let i = 0; i < 8; i++) {
        const cx = 0.08 + s.r() * 0.84;
        const cy = 0.04 + s.r() * 0.3;
        if (env.map && !inSky(env.map, cx, cy, 0.12)) continue;
        const l = env.map ? lumAt(env.map, cx, cy) : 0;
        if (l < best) [best, x, y] = [l, cx, cy];
      }
      const ang = ((18 + s.r() * 20) * Math.PI) / 180;
      const dir = x > 0.5 ? -1 : 1;
      s.live.push({ t0: t, x: x * w, y: y * h, dx: Math.cos(ang) * dir, dy: Math.sin(ang), v: (520 + s.r() * 320) * u, life: 0.75 + s.r() * 0.45, len: (150 + s.r() * 110) * u, b: 0.7 + s.r() * 0.3 });
      s.next = t + (26 - 20 * env.strength) * (0.6 + s.r() * 0.8);
    }
    if (!s.live.length) {
      if (s.drawn) ctx.clearRect(0, 0, w, h);
      s.drawn = false;
      return;
    }
    ctx.clearRect(0, 0, w, h);
    s.drawn = true;
    const col = env.color ?? [226, 236, 255];
    s.live = s.live.filter((m) => t - m.t0 < m.life);
    for (const m of s.live) {
      const age = t - m.t0;
      const fade = smooth(0, 0.12, age) * (1 - smooth(m.life * 0.5, m.life, age)) * m.b * (0.6 + env.strength * 0.5);
      const hx = m.x + m.dx * m.v * age;
      const hy = m.y + m.dy * m.v * age;
      const tail = m.len * Math.min(1, age / 0.3);
      const g = ctx.createLinearGradient(hx - m.dx * tail, hy - m.dy * tail, hx, hy);
      g.addColorStop(0, css(col, 0));
      g.addColorStop(1, css(lift(col, 0.3), fade));
      ctx.strokeStyle = g;
      ctx.lineWidth = Math.max(1, 1.6 * u);
      ctx.lineCap = "round";
      ctx.beginPath();
      ctx.moveTo(hx - m.dx * tail, hy - m.dy * tail);
      ctx.lineTo(hx, hy);
      ctx.stroke();
      ctx.globalAlpha = fade;
      ctx.drawImage(s.head, hx - s.head.width / 2, hy - s.head.height / 2);
      ctx.globalAlpha = 1;
    }
    keepToSky(ctx, s.mask, w, h);
  },
};

// ---------------------------------------------------------------- lightning

interface Flash {
  t0: number;
  x: number;
  y: number;
  second: number;
  peak2: number;
}

export const lightning: Effect = {
  fps: 30,
  res: 0.5,
  blend: "screen",
  build(env) {
    const r = rng(env.seed * 13 + 9);
    return { r, next: 3 + r() * 3, flash: null as Flash | null, drawn: false, mask: env.map ? skyMask(env.map) : null, grain: speckle(128, 0.24) };
  },
  frame(ctx, t, state, env) {
    const s = state as { r: () => number; next: number; flash: Flash | null; drawn: boolean; mask: HTMLCanvasElement | null; grain: HTMLCanvasElement };
    const { w, h } = env;
    if (!s.flash && t >= s.next) {
      // two soft swells at most, well apart: a storm far off, never a strobe
      s.flash = { t0: t, x: 0.12 + s.r() * 0.76, y: 0.06 + s.r() * 0.22, second: 0.34 + s.r() * 0.16, peak2: 0.45 + s.r() * 0.3 };
      s.next = t + (24 - 14 * env.strength) * (0.6 + s.r() * 0.8);
    }
    if (!s.flash) {
      if (s.drawn) ctx.clearRect(0, 0, w, h);
      s.drawn = false;
      return;
    }
    const f = s.flash;
    const dt = t - f.t0;
    const swell = (d: number) => (d < 0 ? 0 : d < 0.08 ? d / 0.08 : Math.exp(-(d - 0.08) / 0.24));
    const lit = Math.min(1, swell(dt) + f.peak2 * swell(dt - f.second));
    if (dt > 2) s.flash = null;
    ctx.clearRect(0, 0, w, h);
    s.drawn = true;
    if (lit < 0.004) return;
    const col = env.color ?? [214, 222, 255];
    const k = (0.2 + env.strength * 0.32) * lit;
    const g = ctx.createRadialGradient(f.x * w, f.y * h, 0, f.x * w, f.y * h, w * 0.75);
    g.addColorStop(0, css(lift(col, 0.4), 0.9));
    g.addColorStop(0.3, css(col, 0.38));
    g.addColorStop(1, css(col, 0));
    ctx.globalAlpha = k;
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, w, h);
    // and the whole sky lifts a little with it
    const sky = ctx.createLinearGradient(0, 0, 0, h * 0.6);
    sky.addColorStop(0, css(col, 1));
    sky.addColorStop(1, css(col, 0));
    ctx.globalAlpha = k * 0.3;
    ctx.fillStyle = sky;
    ctx.fillRect(0, 0, w, h * 0.6);
    keepToSky(ctx, s.mask, w, h);
    dither(ctx, s.grain, w, h);
  },
};

// ---------------------------------------------------------------- cloud shadows

export const clouds: Effect = {
  fps: 12,
  res: 0.25,
  blend: "multiply",
  build(env) {
    // shade the color of the land in shadow: its own tone, much darker, a little cooler
    const land = env.map ? (average(env.map, [0, 0.45, 1, 1]) ?? [90, 100, 90]) : ([90, 100, 90] as RGB);
    const shade = env.color ?? mixRGB(land.map((c) => c * 0.35) as RGB, [20, 28, 48], 0.45);
    const big = noiseTexture(256, 96, { seed: env.seed * 3 + 1, cells: 3, rows: 2, octaves: 4, rgb: shade, shape: (n) => smooth(0.46, 0.8, n) });
    const small = noiseTexture(256, 96, { seed: env.seed * 3 + 2, cells: 5, rows: 3, octaves: 3, rgb: shade, shape: (n) => smooth(0.52, 0.82, n) * 0.6 });
    const land0 = env.y ?? (env.map ? horizon(env.map) : 0.42);
    return { big, small, land: land0 };
  },
  frame(ctx, t, state, env) {
    const { big, small, land } = state as { big: HTMLCanvasElement; small: HTMLCanvasElement; land: number };
    const { w, h } = env;
    ctx.clearRect(0, 0, w, h);
    const k = 0.14 + env.strength * 0.3;
    // laid on the land, which runs from the horizon to the foot of the screen: shadows near the
    // horizon are far away, so they are drawn flatter, in two bands, the far one squashed
    const top = h * Math.max(0.2, land - 0.05);
    const depth = h - top;
    const lay = (tex: HTMLCanvasElement, tw: number, speed: number, a: number) => {
      const x = -wrap(t * speed * w, tw);
      ctx.globalAlpha = a;
      for (const dx of [x, x + tw]) {
        ctx.drawImage(tex, 0, 0, tex.width, tex.height * 0.35, dx, top, tw, depth * 0.3);
        ctx.drawImage(tex, 0, tex.height * 0.35, tex.width, tex.height * 0.65, dx, top + depth * 0.3, tw, depth * 0.7);
      }
    };
    lay(big, w * 1.8, 0.0045, k);
    lay(small, w * 1.15, 0.0075, k * 0.55);
    // shadows fall on the land, not on the sky above it
    ctx.globalAlpha = 1;
    ctx.globalCompositeOperation = "destination-in";
    const g = ctx.createLinearGradient(0, h * (land - 0.06), 0, h * (land + 0.1));
    g.addColorStop(0, "rgba(0,0,0,0)");
    g.addColorStop(1, "rgba(0,0,0,1)");
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, w, h);
    ctx.globalCompositeOperation = "source-over";
  },
};

